import './default-theme.processed.css';
import './index.css';
import './elasticnote-design.css';
import './saas.css';
import './ws-elasticnote.css';
import './private-app.css';

import { consumePwaLaunchParams } from '@elasticnote/app/pwa-install';
import { ThemeManager } from '@elasticnote/color-scheme-manager';
import { APP_BUILD_ID, APP_BUILD_TIME } from '@elasticnote/config';
import { THEME_MANAGER_CONFIG } from '@elasticnote/constants';
import {
  createEditorSaveCoordinator,
  initializeServices,
} from '@elasticnote/initialize-services';
import { Logger } from '@elasticnote/logger';
import { t } from '@elasticnote/translations';
import { createStore } from 'jotai';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { setupAppBuildGuard } from './app-build-guard';
import { setupRootEmitter } from './setup-root-emitter';
import { ElasticnoteSiteTelemetry } from './site-telemetry';
import {
  installElasticnoteLocationChangeBridge,
  resolveElasticnoteBootSurface,
  ELASTICNOTE_LOCATION_CHANGE_EVENT,
  type ElasticnoteBootSurface,
} from './ws-boot-surface';

const isDebug =
  window.location.hostname === 'localhost' ||
  window.location.search.includes('debug=true');

const startupLogger = new Logger('', isDebug ? 'debug' : 'info');
const editorSaveCoordinator = createEditorSaveCoordinator();

let editorModulePromise: ReturnType<typeof importEditorModule> | undefined;
let saasModulePromise: ReturnType<typeof importSaasModule> | undefined;
function importEditorModule() { return import('@elasticnote/app'); }
function importSaasModule() { return import('@elasticnote/app/saas'); }
function loadEditorModule() {
  if (!editorModulePromise) editorModulePromise = importEditorModule();
  return editorModulePromise;
}
function loadSaasModule() {
  if (!saasModulePromise) saasModulePromise = importSaasModule();
  return saasModulePromise;
}

const LazyElasticnoteEditorApp = React.lazy(async () => {
  const module = await loadEditorModule();
  return { default: module.App };
});

const LazyElasticnoteSaasApp = React.lazy(async () => {
  const module = await loadSaasModule();
  return { default: module.SaasApp };
});

async function preloadElasticnoteSurface(surface: Exclude<ElasticnoteBootSurface, 'home' | 'projects'>) {
  if (surface === 'editor') await loadEditorModule();
  else await loadSaasModule();
}

function installBootstrapProgress() {
  const existing = document.querySelector<HTMLElement>('[data-elasticnote-bootstrap-screen]');
  if (existing) {
    document.body.setAttribute('aria-busy', 'true');
    return existing;
  }
  const element = document.createElement('div');
  element.id = 'elasticnote-bootstrap-screen';
  element.dataset.elasticnoteBootstrapScreen = 'true';
  element.setAttribute('aria-hidden', 'true');
  element.innerHTML = '<i></i>';
  document.body.append(element);
  document.body.setAttribute('aria-busy', 'true');
  return element;
}

function finishBootstrapProgress(element: HTMLElement) {
  if (document.documentElement.dataset.elasticnoteAppReady === 'true') return;
  document.documentElement.dataset.elasticnoteAppReady = 'true';
  document.body.removeAttribute('aria-busy');
  const remove = () => { if (element.isConnected) element.remove(); };
  element.addEventListener('transitionend', remove, { once: true });
  window.setTimeout(remove, 350);
}

async function waitForElasticnoteFonts(_logger: Logger) {
  // Font loading is intentionally non-blocking. Brand faces use font-display: swap
  // and optional specialist/display faces may be absent on a deployment. Waiting on
  // explicit per-face font loads here caused boot-time network errors for optional files
  // even though the CSS fallback stacks were valid.
  if ('fonts' in document) void document.fonts.ready.catch(() => undefined);
}

async function main(logger: Logger) {
  installElasticnoteLocationChangeBridge(window);
  registerElasticnoteServiceWorker(logger);
  // Consume PWA launch params (?launch=, ?shortcut=) before any service is
  // constructed: the router captures `window.location.search` at creation
  // and re-emits it on every navigation, so a param still present here
  // would be re-added to the URL and replayed on reload. Idempotent, so the
  // in-app fallback consumer stays harmless.
  consumePwaLaunchParams(window);

  // Capture the browser entry intent before initializeServices(). The hash
  // router is constructed during service initialization and startup recovery
  // may normalize browser history before React is mounted. A valid /ws hash
  // deep link must therefore remain authoritative even if location.hash changes
  // while services are starting.
  const bootLocation = {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };

  const bootSurface = resolveElasticnoteBootSurface(bootLocation.pathname, bootLocation.hash);
  if (bootSurface === 'home') {
    window.location.replace('/');
    return;
  }
  if (bootSurface === 'projects') {
    window.location.replace('/workspace');
    return;
  }

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  // Keep the SEO prerender in the document, but cover it with the inline bootstrap screen
  // until the route chunk, application CSS, core services and required brand fonts are ready.
  const bootstrapProgress = installBootstrapProgress();
  const fontPreflight = waitForElasticnoteFonts(logger).finally(() => { document.documentElement.dataset.elasticnoteFontsReady = 'true'; });
  const surfacePreload = preloadElasticnoteSurface(bootSurface === 'editor' ? 'editor' : 'saas');

  const abortController = new AbortController();
  const tabId = `tab_${Math.random().toString(36).substr(2, 9)}`;

  const rootEmitter = setupRootEmitter(
    'elasticnote_io_channel',
    tabId,
    logger,
    abortController.signal,
  );
  const appBuildGuard = setupAppBuildGuard({
    rootEmitter,
    appBuild: { id: APP_BUILD_ID, builtAt: APP_BUILD_TIME },
    tabId,
    logger: logger.child('app-build-guard'),
    abortSignal: abortController.signal,
  });

  const store = createStore();
  const themeManager = new ThemeManager(THEME_MANAGER_CONFIG);
  let services: Awaited<ReturnType<typeof initializeServices>>;
  try {
    services = await initializeServices(
      logger,
      rootEmitter,
      store,
      themeManager,
      abortController.signal,
      editorSaveCoordinator,
    );
  } catch (error) {
    bootstrapProgress.remove();
    abortController.abort();
    throw error;
  }
  try {
    await Promise.all([surfacePreload, fontPreflight]);
  } catch (error) {
    bootstrapProgress.remove();
    document.body.removeAttribute('aria-busy');
    abortController.abort();
    throw error;
  }

  const currentSurface = resolveElasticnoteBootSurface(window.location.pathname, window.location.hash);

  // If service initialization normalized the browser URL, restore the exact
  // editor deep link captured at boot before React mounts. Hash fragments never
  // reach Apache, so this is entirely browser-local and preserves the selected
  // workspace/file.
  if (bootSurface === 'editor' && currentSurface !== 'editor') {
    window.history.replaceState(
      window.history.state,
      '',
      `${bootLocation.pathname}${bootLocation.search}${bootLocation.hash}`,
    );
  }

  const initialSurface = resolveElasticnoteBootSurface(window.location.pathname, window.location.hash);
  if (initialSurface === 'home') {
    window.location.replace('/');
    return;
  }
  if (initialSurface === 'projects') {
    window.location.replace('/workspace');
    return;
  }

  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <ElasticnoteRootApplication
        initialSurface={bootSurface === 'editor' ? 'editor' : initialSurface}
        logger={logger}
        services={services}
        store={store}
        rootEmitter={rootEmitter}
        onApplicationReady={() => finishBootstrapProgress(bootstrapProgress)}
      />
    </StrictMode>,
  );

  // Services (including the stale-tab listener) must be mounted before a
  // running tab can reply to this announcement.
  appBuildGuard.start();

  if (isDebug) {
    (window as any).services = services;
  }

  abortController.signal.addEventListener('abort', () => {
    root.unmount();
  });

  rootEmitter.on(
    'event::app:reload-ui',
    () => {
      logger.info('-------------Reloading UI-------------');
      abortController.abort();
      queueMicrotask(() => {
        void main(logger).catch((error) => {
          handleStartupFailure(error, logger);
        });
      });
    },
    abortController.signal,
  );
}


type ElasticnoteRootApplicationProps = {
  initialSurface: Exclude<ElasticnoteBootSurface, 'home' | 'projects'>;
  logger: Logger;
  services: Awaited<ReturnType<typeof initializeServices>>;
  store: ReturnType<typeof createStore>;
  rootEmitter: ReturnType<typeof setupRootEmitter>;
  onApplicationReady: () => void;
};

/**
 * Live top-level route boundary. This is deliberately outside SaasApp/App so a
 * history/hash transition to /ws can never be rendered by the SaaS 404 route.
 */
function ElasticnoteRootApplication({
  initialSurface,
  logger,
  services,
  store,
  rootEmitter,
  onApplicationReady,
}: ElasticnoteRootApplicationProps) {
  const [surface, setSurface] = React.useState<Exclude<ElasticnoteBootSurface, 'home' | 'projects'>>(initialSurface);

  React.useEffect(() => {
    const synchronize = () => {
      const next = resolveElasticnoteBootSurface(window.location.pathname, window.location.hash);
      if (next === 'home') {
        window.location.replace('/');
        return;
      }
      if (next === 'projects') {
        window.location.replace('/workspace');
        return;
      }
      setSurface(next);
    };
    window.addEventListener('hashchange', synchronize);
    window.addEventListener('popstate', synchronize);
    window.addEventListener(ELASTICNOTE_LOCATION_CHANGE_EVENT, synchronize);
    return () => {
      window.removeEventListener('hashchange', synchronize);
      window.removeEventListener('popstate', synchronize);
      window.removeEventListener(ELASTICNOTE_LOCATION_CHANGE_EVENT, synchronize);
    };
  }, []);

  document.documentElement.dataset.elasticnoteSurface = surface;
  const RootApplication = surface === 'editor' ? LazyElasticnoteEditorApp : LazyElasticnoteSaasApp;
  return (
    <>
      <React.Suspense fallback={<SurfaceLoadingSkeleton />}>
        <RootApplication
          logger={logger}
          services={services}
          store={store}
          rootEmitter={rootEmitter}
        />
        <ApplicationPaintReady onReady={onApplicationReady} />
      </React.Suspense>
      <ElasticnoteSiteTelemetry />
    </>
  );
}


function ApplicationPaintReady({ onReady }: { onReady: () => void }) {
  React.useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(onReady);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [onReady]);
  return null;
}


function SurfaceLoadingSkeleton() {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 250);
    return () => window.clearTimeout(timer);
  }, []);
  if (!visible) return null;
  return <main className="interactive-route-skeleton interactive-surface-loading-skeleton" aria-hidden="true">
    <div className="interactive-route-skeleton-heading"><i /><i /></div>
    <div className="interactive-route-skeleton-grid"><span /><span /><span /></div>
  </main>;
}


function handleStartupFailure(error: unknown, logger: Logger) {
  logger.error('Unable to start Elasticnote', error);
  renderStartupError(error);
}

export function renderStartupError(error: unknown) {
  document.documentElement.dataset.elasticnoteAppReady = 'true';
  document.body.removeAttribute('aria-busy');
  document.querySelector<HTMLElement>('[data-elasticnote-bootstrap-screen]')?.remove();
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Unable to show startup failure because #root is missing');
    return;
  }

  createRoot(rootElement).render(
    <StrictMode>
      <StartupErrorView error={error} />
    </StrictMode>,
  );
}

function StartupErrorView({ error }: { error: unknown }) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : t.app.pageStartupError.description;

  return (
    <main
      role="alert"
      aria-labelledby="startup-error-title"
      className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground"
    >
      <section className="w-full max-w-xl rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
        <h1 id="startup-error-title" className="font-semibold text-2xl">
          {t.app.pageStartupError.title}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {t.app.pageStartupError.description}
        </p>
        <details className="mt-4 rounded-md border bg-muted/40 p-3">
          <summary className="cursor-pointer font-medium text-sm">
            {t.app.pageStartupError.detailsLabel}
          </summary>
          <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap break-words text-muted-foreground text-xs">
            {message}
          </pre>
        </details>
        <button
          className="mt-5 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground text-sm"
          type="button"
          onClick={() => window.location.reload()}
        >
          {t.app.pageStartupError.reloadButton}
        </button>
      </section>
    </main>
  );
}


function registerElasticnoteServiceWorker(logger: Logger): void {
  if (!('serviceWorker' in navigator)) return;
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') return;
  void navigator.serviceWorker
    .register(`/elasticnote-texlive-sw.js?build=${encodeURIComponent(APP_BUILD_TIME)}`, { scope: '/', updateViaCache: 'none' })
    .catch((error) => logger.warn('Unable to register Elasticnote offline service worker', error));
}

// Start only after all module-level bootstrap constants have initialized.
void main(startupLogger).catch((error) => {
  handleStartupFailure(error, startupLogger);
});
